import {
  refreshAssetsRowsDescriptor,
  setVehiclesHistoryRowsDescriptor,
} from './assets.actions';
import { LicenseUser } from '../../models/license-user';
import { AssetVehicle } from '../../models/asset-vehicle';
import { HistoricalNotification } from '../../models/historical-notification';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { AssetsImportResponse } from '../../models/assets-import-response';
import { FilterAssetsSelectOption } from '../../features/assets/components/filter-assets-dropdown/models/filter-assets-select-option';
import { dispatch } from '@ngneat/effects';
import { createStore, emitOnce, select, withProps } from '@ngneat/elf';
import {
  getAllEntities,
  selectAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import {
  clearRequestsStatus,
  createRequestsStatusOperator,
  getRequestStatus,
  joinRequestResult,
  selectIsRequestPending,
  selectRequestStatus,
  updateRequestStatus,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import {
  LoadingRowsDescriptor,
  RowDescriptor,
  RowsDescriptor,
  SelectAllState,
  SortDirection,
  UpdateRowsDescriptor,
} from '@tmx/operandi';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
} from 'rxjs';
import {
  DriverVehicleLink,
  FleetVehicleInformation,
  SmsInviteStatus,
  VehicleDriverLink,
} from '@tmx/data-access';
import { trackRequest } from '@tmx/core/elf-utils';

type RequestKeys = 'fetch-csv' | 'historical-vehicles';

interface AssetProps {
  totalAssets: number;
  hasAssets: boolean;
  offset: number;
  sortExpression: string;
  sortDirection: '' | 'asc' | 'desc';
  query: string;
  filterAssetsSelectOption: FilterAssetsSelectOption | null;
  assetsRowsDescriptor: RowsDescriptor;
  selection: Map<string, LicenseUser>;
  selectionScope: Map<string, LicenseUser>;
  vehiclesHistoryRowsDescriptor: RowsDescriptor;
  fleetInformationMap: Map<string, FleetVehicleInformation>;
  smsStatusesMap: Map<string, SmsInviteStatus>;
  historicalVehicles: AssetVehicle[];
  historicalVehiclesOffset: number;
  filterByAssetIds: string[];
  historicalNotifications: HistoricalNotification[];
  linkedVehicleMap: Map<string, DriverVehicleLink>;
  linkedDriverMap: Map<string, VehicleDriverLink>;
  importResponse: AssetsImportResponse;
  copilotVersions: string[];
}

const assetStore = createStore(
  { name: 'asset' },
  withProps<AssetProps>({
    totalAssets: 0,
    hasAssets: false,
    offset: 0,
    sortExpression: '',
    sortDirection: SortDirection.None,
    query: '',
    filterAssetsSelectOption: null,
    assetsRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    selection: new Map<string, LicenseUser>(),
    selectionScope: new Map<string, LicenseUser>(),
    vehiclesHistoryRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    fleetInformationMap: new Map<string, FleetVehicleInformation>(),
    smsStatusesMap: new Map<string, SmsInviteStatus>(),
    historicalVehicles: [],
    historicalVehiclesOffset: 0,
    filterByAssetIds: [],
    historicalNotifications: [],
    linkedVehicleMap: new Map<string, DriverVehicleLink>(),
    linkedDriverMap: new Map<string, VehicleDriverLink>(),
    importResponse: new AssetsImportResponse(),
    copilotVersions: [],
  }),
  withEntities<LicenseUser, 'entityId'>({ idKey: 'entityId' }),
  withRequestsStatus<
    | 'asset-import'
    | 'fleet-information'
    | 'linked-data'
    | 'load-assets'
    | 'load-historical-notifications'
    | 'load-initial-selection'
    | 'load-selection-scope-resize-data'
    | 'load-selection-scope-select-all'
    | 'samsara-invite'
    | 'sms-information'
  >()
);

export const trackAssetsRequests = <T>(keys: RequestKeys[]) =>
  trackRequest<T, RequestKeys>(keys);

export const joinAssetsRequests = <T>(keys: RequestKeys[]) =>
  joinRequestResult<T>(keys);

export const fetchAssetsCsvRequest$ = assetStore.pipe(
  joinAssetsRequests(['fetch-csv']),
  map(({ status, fetchStatus, isSuccess }) => ({
    status,
    fetchStatus,
    isSuccess,
  })),
  distinctUntilChanged()
);

export const historicalVehiclesRequest$ = assetStore.pipe(
  joinAssetsRequests(['historical-vehicles']),
  map(({ status, fetchStatus, isSuccess }) => ({
    status,
    fetchStatus,
    isSuccess,
  })),
  distinctUntilChanged()
);

export const trackAssetsRequestsStatus =
  createRequestsStatusOperator(assetStore);

export type LoadSelectionScopeReason = 'resize-data' | 'select-all';

export const assetStoreSnapshot = () => assetStore.getValue();

export const loadAssetsComplete$ = assetStore.pipe(
  selectRequestStatus('load-assets'),
  filter(({ value }) => value === 'error' || value === 'success')
);

export const loading$ = combineLatest([
  assetStore.pipe(selectIsRequestPending('load-assets')),
  assetStore.pipe(selectIsRequestPending('load-initial-selection')),
  assetStore.pipe(selectIsRequestPending('load-selection-scope-resize-data')),
]).pipe(
  map(
    ([loadingData, loadingInitialSelection, loadingSelectionScope]) =>
      loadingData || loadingInitialSelection || loadingSelectionScope
  )
);

export const selectAllStatus$ = assetStore.pipe(
  selectRequestStatus('load-selection-scope-select-all')
);

export const fleetInformationStatus$ = assetStore.pipe(
  selectRequestStatus('fleet-information')
);

export const smsInformationStatus$ = assetStore.pipe(
  selectRequestStatus('sms-information')
);

export const linkedDataStatus$ = assetStore.pipe(
  selectRequestStatus('linked-data')
);

export const samsaraInviteStatus$ = assetStore.pipe(
  selectRequestStatus('samsara-invite')
);

export const assets$ = assetStore.pipe(selectAllEntities());

export const hasAssets$ = assetStore.pipe(select(({ hasAssets }) => hasAssets));

export const assetsRowsDescriptor$ = assetStore.pipe(
  select(({ assetsRowsDescriptor }) => assetsRowsDescriptor)
);

const selectedAssetsSource$ = new BehaviorSubject<LicenseUser[]>([]);
export const selectedAssets$ = selectedAssetsSource$.asObservable();

export const filterAssetsSelectOption$ = assetStore.pipe(
  select(({ filterAssetsSelectOption }) => filterAssetsSelectOption)
);

export const vehiclesHistoryRowsDescriptor$ = assetStore.pipe(
  select(({ vehiclesHistoryRowsDescriptor }) => vehiclesHistoryRowsDescriptor)
);

export const loadingHistoricalNotifications$ = assetStore.pipe(
  selectIsRequestPending('load-historical-notifications')
);

export const didLoadingHistoricalNotificationsError$ = assetStore.pipe(
  selectRequestStatus('load-historical-notifications'),
  map(({ value }) => value === 'error')
);

export const historicalNotifications$ = assetStore.pipe(
  select(({ historicalNotifications }) => historicalNotifications)
);

export const notificationRows$ = combineLatest([
  loadingHistoricalNotifications$,
  historicalNotifications$,
]).pipe(
  map(([loading, notifications]) =>
    loading
      ? new LoadingRowsDescriptor()
      : notifications.length === 0
        ? EMPTY_ROWS_DESCRIPTOR
        : new UpdateRowsDescriptor<HistoricalNotification, number>(
            notifications.map(
              (notification, index) =>
                new RowDescriptor(notification, () => index)
            ),
            0,
            notifications.length,
            0,
            SelectAllState.Deselected
          )
  )
);

export const importResponse$ = assetStore.pipe(
  select((state) => state.importResponse)
);

export const copilotVersions$ = assetStore.pipe(
  select(({ copilotVersions }) => copilotVersions)
);

export function updateSamsaraInviteStatus() {
  assetStore.update(updateRequestStatus('samsara-invite', 'success'));
}

export function loadingSnapshot() {
  const loadingData =
    assetStore.query(getRequestStatus('load-assets')).value === 'pending';
  const loadingInitialSelection =
    assetStore.query(getRequestStatus('load-initial-selection')).value ===
    'pending';
  const loadingSelectionScope =
    assetStore.query(getRequestStatus('load-selection-scope-resize-data'))
      .value === 'pending';
  return loadingData || loadingInitialSelection || loadingSelectionScope;
}

export function selectingAllSnapshot() {
  return (
    assetStore.query(getRequestStatus('load-selection-scope-select-all'))
      .value === 'pending'
  );
}

export function updateAssets(
  assets: LicenseUser[],
  totalAssets: number,
  hasAssets: boolean
) {
  assetStore.update(
    (state) => ({
      ...state,
      totalAssets,
      hasAssets,
    }),
    setEntities(assets)
  );
}

export function updateLoadingAssetsStatus(loading: boolean) {
  assetStore.update(
    updateRequestStatus('load-assets', loading ? 'pending' : 'success')
  );
  dispatch(refreshAssetsRowsDescriptor());
}

export function updateFleetInformationMap(
  fleetInformation: FleetVehicleInformation[]
) {
  const { fleetInformationMap } = assetStore.getValue();
  for (const fleet of fleetInformation) {
    fleetInformationMap.set(fleet.deviceId, fleet);
  }
  assetStore.update(
    (state) => ({
      ...state,
      fleetInformationMap,
    }),
    updateRequestStatus('fleet-information', 'success')
  );
}

export function updateSmsStatusesMap(smsStatuses: SmsInviteStatus[]) {
  const { smsStatusesMap } = assetStore.getValue();
  for (const smsStatus of smsStatuses) {
    smsStatusesMap.set(smsStatus.entityId, smsStatus);
  }
  assetStore.update(
    (state) => ({
      ...state,
      smsStatusesMap,
    }),
    updateRequestStatus('sms-information', 'success')
  );
}

export function updateLinkedVehicleMap(linkedData: DriverVehicleLink[]) {
  const linkedVehicleMap = assetStore.getValue().linkedVehicleMap;
  for (const data of linkedData) {
    linkedVehicleMap.set(data.driverId, data);
  }
  assetStore.update(
    (state) => ({
      ...state,
      linkedVehicleMap,
    }),
    updateRequestStatus('linked-data', 'success')
  );
}

export function updateLinkedDriverMap(linkedData: VehicleDriverLink[]) {
  const linkedDriverMap = assetStore.getValue().linkedDriverMap;
  for (const data of linkedData) {
    linkedDriverMap.set(data.vehicleDriverLinks[0].vehicleId, data);
  }
  assetStore.update(
    (state) => ({
      ...state,
      linkedDriverMap,
    }),
    updateRequestStatus('linked-data', 'success')
  );
}

export function updateOffset(offset: number) {
  assetStore.update((state) => ({
    ...state,
    offset,
  }));
}

export function updateSort(
  sortExpression: string,
  sortDirection: '' | 'asc' | 'desc'
) {
  assetStore.update((state) => ({
    ...state,
    sortExpression,
    sortDirection,
  }));
}

export function updateQuery(query: string) {
  assetStore.update((state) => ({
    ...state,
    query,
  }));
}

export function updateFilterAssetsSelectOption(
  filterAssetsSelectOption: FilterAssetsSelectOption | null
) {
  assetStore.update((state) => ({
    ...state,
    filterAssetsSelectOption,
  }));
}

export function updateAssetsRowsDescriptor(
  assetsRowsDescriptor: RowsDescriptor
) {
  assetStore.update((state) => ({
    ...state,
    assetsRowsDescriptor,
  }));
}

export function updateVehiclesHistoryRowsDescriptor(
  vehiclesHistoryRowsDescriptor: RowsDescriptor
) {
  assetStore.update((state) => ({
    ...state,
    vehiclesHistoryRowsDescriptor,
  }));
}

export function clearAssetsStore() {
  emitOnce(() => {
    assetStore.update(clearRequestsStatus());
    assetStore.reset();
  });
}

const filterByAssetsImportCancelledSource$ = new Subject<void>();
export const assetDeleted$ = new Subject<void>();
export const assetCreatedOrUpdated$ = new Subject<void>();
export const filterByAssetsImportCancelled$ =
  filterByAssetsImportCancelledSource$.asObservable();

export function updateLoadingSelectionScopeStatus(
  reason: LoadSelectionScopeReason,
  loading: boolean
) {
  assetStore.update(
    updateRequestStatus(
      reason === 'resize-data'
        ? 'load-selection-scope-resize-data'
        : 'load-selection-scope-select-all',
      loading ? 'pending' : 'success'
    )
  );
  dispatch(refreshAssetsRowsDescriptor());
}

export function updateHistoricalVehicles(historicalVehicles: AssetVehicle[]) {
  assetStore.update((state) => ({
    ...state,
    historicalVehicles,
  }));
  dispatch(setVehiclesHistoryRowsDescriptor({ loading: false }));
}

export function updateHistoricalVehiclesOffset(
  historicalVehiclesOffset: number
) {
  assetStore.update((state) => ({
    ...state,
    historicalVehiclesOffset,
  }));
}

export function updateSelectedAssets() {
  const { selection } = assetStoreSnapshot();
  selectedAssetsSource$.next(Array.from(selection.values()));
}

export function updateFilterByAssets(filterByAssetIds: string[]) {
  assetStore.update((state) => ({
    ...state,
    filterByAssetIds,
  }));
}

export function updateCancelFilterByAssetsImport() {
  filterByAssetsImportCancelledSource$.next();
}

export function updateHistoricalNotifications(
  historicalNotifications: HistoricalNotification[],
  failed = false
) {
  assetStore.update(
    (state) => ({
      ...state,
      historicalNotifications,
    }),
    failed
      ? updateRequestStatus('load-historical-notifications', 'error', null)
      : updateRequestStatus('load-historical-notifications', 'success')
  );
}

export function updateAssetImportStatus() {
  assetStore.update(updateRequestStatus('asset-import', 'success'));
}

export function updateImportResponse(importResponse: AssetsImportResponse) {
  assetStore.update((state) => ({
    ...state,
    importResponse,
  }));
}

export function updateAssetDeleted() {
  assetDeleted$.next();
}

export function updateAssetCreatedOrUpdated() {
  assetCreatedOrUpdated$.next();
}

export function updateCopilotVersions(copilotVersions: string[]) {
  assetStore.update((state) => ({
    ...state,
    copilotVersions,
  }));
}

export const assetStorEntities = () => assetStore.query(getAllEntities());
